<template>
  <div class="modal" :class="{'is-active': active}">
    <div class="modal-background" />
    <div class="modal-card" :qa="active ? 'attach-modal' : ''">
      <button @click="close" class="delete modal-close" aria-label="close" qa="close-attach-modal"></button>
      <div class="modal-header title is-uppercase is-left" qa="modal-header">
        <i class="fa fa-file"/>
        <span>Upload Rubric</span>
      </div>
      <section class="modal-card-body">
        <div v-if="!attaching" class="control">
          <div class="columns is-align-items-center">
            <!-- Show File -->
            <div class="column" :class="{ 'column-adjust' : !file }">
              <div class="columns is-mobile">
                <div class="column file-input-container">
                  <button class="button is-fullwidth" @click="$refs.file.click()" qa="choose-file-button">
                    <span v-if="file">
                    <!-- Desktop view -->
                      <span class="is-hidden-mobile">
                        {{ limitStringLength(file.name, strLimitDesktop) }}
                      </span>
                      <span class="is-hidden-tablet">
                        <!-- Mobile view under 450 width -->
                        <span v-if="viewPortWidth < 450">
                          {{ limitStringLength(file.name, strLimitSmallMobile) }}
                        </span>
                        <!-- Mobile view between 768 and 450 width -->
                        <span v-else-if="isMobile">
                          {{ limitStringLength(file.name, strLimitMobile) }}
                        </span>
                      </span>
                    </span>
                    <span v-else>Choose File</span>
                  </button>
                  <input type="file" id="file" ref="file"
                         :accept="this.acceptedFormat"
                         @change="selectFile"
                  />
                </div>
                <div class="column is-narrow" v-if="file">
                  <button v-if="file" class="button is-danger is-full-width" @click="file = null; error = ''"><span class="icon"><i
                      class="fal fa-trash"></i></span></button>
                </div>
              </div>
            </div>
            <p v-if="!file" class="type-message">Please select the scored rubric for this project.<br>Only .xlsx file types are accepted.</p>
          </div>
        </div>
        <div v-if="error" class="notification is-danger mt-4" qa="error-msg">{{ error }}</div>
        <div v-if="attaching">
          <span v-if="!progress" class="loading-container is-flex">
            <span class="loader"></span>&nbsp;&nbsp;{{ attachingMessage }}...
          </span>
          <progress v-if="progress" class="progress is-primary is-marginless" :value="progress.percentage" max="100">
            {{ progress.percentage }}%
          </progress>
          <span v-if="progress" class="help is-pulled-right">{{ progress.percentage }}% Complete</span>
        </div>
      </section>
      <div class="button-container is-flex">
        <div class="cancel-and-save">
          <div v-if="!attaching"
               @click="close"
               class="button cancel is-primary is-outlined is-uppercase"
               qa="cancel-attach-button"
               tabindex="0"
               @keypress.enter.space="close"> {{ $t("cancel") }}
          </div>
          <div class="save button is-primary is-uppercase"
               :disabled="attaching"
               @click="uploadFile"
               tabindex="0"
               @keypress.enter.space="uploadFile">
            <span :qa="active ? 'attach-btn' : ''">Upload</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import i18n from '@/i18n'
import breakpoints from '@/libs/breakpoints'
import verge from 'verge'

const maxFileSize = 50000000 // 50MB
const maxSizeWarning = i18n.t('attachmentsWarnings.maxFile')
const fileTypeWarning = i18n.t('attachmentsWarnings.fileSupport')
// const defaultError = 'Unable to upload. Please try again.'

export default {
  props: ['projectID'],
  data: function () {
    return {
      file: null,
      link: '',
      error: '',
      attachment: null,
      attaching: false,
      progress: null,
      attachingMessage: i18n.t('projectPage.uploading'),
      timerId: null,
      strLimitDesktop: 50,
      strLimitMobile: 35,
      strLimitSmallMobile: 20
    }
  },
  computed: {
    isMobile: function () {
      return breakpoints.isMobile()
    },
    viewPortWidth: function () {
      return verge.viewportW()
    },
    active: function () {
      return this.$store.getters.modalIsVisible('rubricAttach')
    },
    acceptedFormat: function () {
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }
  },
  mounted: function () {
    this.isDownloadable = false
  },
  methods: {
    limitStringLength: function (value, strSize) {
      if (typeof value === 'string' && value.length > strSize) {
        value = value.slice(0, strSize) + '...'
      }
      return value
    },
    close: function (success) {
      this.reset()
      this.$store.dispatch('hideRubricAttachModal')
    },
    reset: function () {
      this.file = null
      this.error = ''
      this.attaching = false
      if (this.$refs.file) {
        this.$refs.file.value = ''
      }
    },
    selectFile: function (event) {
      if (this.$refs.file.value) {
        const file = event.target.files[0]
        this.$refs.file.value = ''
        this.checkFile(file)
      }
    },
    checkFile: function (file) {
      if (file) {
        if (file.size > maxFileSize) {
          this.error = maxSizeWarning
          this.file = file
          this.attaching = false
        } else if (file.type !== this.acceptedFormat) {
          this.error = fileTypeWarning
          this.file = file
          this.attaching = false
        } else {
          this.error = ''
          this.file = file
          this.attaching = false
        }
      } else {
        console.error('Rubric Upload:: checkFile has no file')
      }
    },
    uploadFile: function () {
      if (this.attaching || this.error) {
        return false
      }
      if (this.file) {
        this.attaching = true
        const rubricFileFormData = new FormData()
        rubricFileFormData.append('rubric', this.file)
        this.$store.dispatch('uploadJudgingRubric', { projectID: this.projectID, rubricFile: rubricFileFormData }).then((response) => {
        }, (error) => {
          this.error = error.message
        }).finally(() => {
          this.attaching = false
          if (!this.error) {
            this.$store.dispatch('getJudgePairings', { projectID: this.projectID })
            this.close(true)
          }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';

.avatar-close-button {
  position: absolute;
  right: 0;
}
.avatar-container {
  padding-top: 1rem;
  text-align: center;
}
input[type="file"] {
  display: none;
}

.checkboxes {
  display: inline-block;
  padding-top: 0.3rem;
}

.type-message {
  font-size: 0.9rem;
}

.modal-card {
  padding: 2rem;
  background-color: white;
  max-width: 600px;
}

.modal-close {
  position: absolute;
  right: 1.5rem;
  top: 1.5rem;
  background-color: white;
  color: $grey-light;
}

.modal-close::before, .modal-close::after {
  background-color: $grey-light;
}

.drop-target {
  border: dashed 3px darkgray;
  padding: 1.5rem;
  text-align: center;
  font-size: 2rem;

  &.over {
    background: lightgray;
  }
}

.file-input-container {
  overflow: hidden;
}

.column-adjust {
  flex-grow: 0.95;
  padding-right: 0.75rem;
}

.modal-card-body {
  padding: 30px 0 10px 0;
  overflow: hidden;
}

.modal-header {
  font-size: .9rem;
  padding-bottom: 20px;
  margin-bottom: 0;
  border-bottom: 1px solid $grey-lighter;
}

.modal-header:before {
  content: "";
  background: $primary;
  position: absolute;
  top: 68px;
  height: 1px;
  width: 87px;
}

.loading-container {
  align-items: center;
}

.button-container {
  justify-content: flex-end;
  &_form {
    justify-content: space-between !important;
  }
  &_icon {
    margin-right: 5px; margin-top: 5px;
    color: $primary;
  }
  &_text {
    font-size: .8em;
    max-width: 150px;
  }
}

.button:active,
.button:focus,
.button:focus-within {
  border-color: $primary;
  -webkit-box-shadow: 0 0 0 0.125em rgba(234, 0, 42, 0.25);
  box-shadow: 0 0 0 0.125em rgba(234, 0, 42, 0.25);
}

.cancel-and-save {
  div {
    margin-left: .5rem;
    border-radius: 1px;
    margin-top: 20px;
    font-size: .65rem;
    min-width: 72px;
    height: 28px;
  }
}
.notification.is-danger {
  white-space: pre-line;
}
.remove-outline {
  outline: none;
}
</style>
